import { useState, useEffect, useCallback } from 'react'
import { useSelector } from 'react-redux'
import { Grid } from '@mui/material'

import Dialog from './Dialog'
import Select from './Select'
import DatePicker from './DatePicker'
import TimePicker from './TimePicker'
import AutoComplete from './AutoComplete'
import GridCheckbox from './GridCheckbox'
import {
  titlesIscrizioni,
  GIORNI,
  TIPOLOGIE_SPOSTAMENTO,
} from '../settings/settings'
import { getCategorie } from '../hooks/selection-get-functions'
import { sortNumeriGara } from '../hooks/sort-functions'
import {
  useGetAffiliazioniLightQuery,
  useGetCalendariQuery,
  useGetImpostazioniAvanzateQuery,
  useGetImpostazioniContattiQuery,
  useGetIscrizioniQuery,
} from '../hooks/api-slice'
import {
  fromStringToDate,
  fromStringToDecimalHours,
  mailTo,
  formatBodyField,
} from '../hooks/utils-functions'
import GridCircularProgress from './GridCircularProgress'

const AVANZ_FIELDS = [
  'TipologieSpostamento',
  'OreDiffSpostamentoGratuito',
  'GiorniAnticipoSpostamento',
  'OrariPartite',
]

const DialogSpostamento = ({ open, handleClose, triggerFunction }) => {
  const { admin, codiceUtente } = useSelector(state => state.home.infoUtente)
  const { data: avanzate } = useGetImpostazioniAvanzateQuery(AVANZ_FIELDS)
  const { data: contatti } = useGetImpostazioniContattiQuery()
  const selectedCampionato = useSelector(
    state => state.selection.selectedCampionato,
  )
  const selectedCampionatoGironiPubblicati = useSelector(
    state => state.selection.selectedCampionatoGironiPubblicati,
  )

  const { data: iscrizioniLight } = useGetIscrizioniQuery(
    { campionato: selectedCampionato, iFilter: 'light' },
    { skip: !(selectedCampionato && selectedCampionatoGironiPubblicati) },
  )
  const { data: affiliazioniLight } = useGetAffiliazioniLightQuery(undefined, {
    skip: !admin,
  })
  const selectedCampionatoSpostamentiGratuiti = useSelector(
    state => state.selection.selectedCampionatoSpostamentiGratuiti,
  )

  const [spostamento, setSpostamento] = useState()

  const { data: availableGare, isLoading } = useGetCalendariQuery(
    { selectedCampionato, codiceUtente, tipologia: 'spostamento', admin },
    { skip: !(selectedCampionato && (admin || codiceUtente)) },
  )

  const {
    TipologieSpostamento,
    OreDiffSpostamentoGratuito,
    GiorniAnticipoSpostamento,
    OrariPartite,
  } = avanzate || {}
  const availableFunctions = Object.keys(TIPOLOGIE_SPOSTAMENTO)?.filter(x =>
    TipologieSpostamento?.includes(x),
  )
  const availableFunctionsLabels = availableFunctions?.map(
    x => TIPOLOGIE_SPOSTAMENTO[x]?.label,
  )
  const availableGiorni = OrariPartite?.map(
    i => (GIORNI.indexOf(i.giorno) + 1) % 7,
  )

  const setSpostamentoField = useCallback(
    (field, value) =>
      setSpostamento(prev => ({ ...prev, [field]: formatBodyField(value) })),
    [],
  )

  const [availableCategorie, setAvailableCategorie] = useState()
  const [availableSquadre, setAvailableSquadre] = useState()
  const [availableGareTeam, setAvailableGareTeam] = useState()
  const [availableGareCategoria, setAvailableGareCategoria] = useState()
  const [availableAffiliazioni, setAvailableAffiliazioni] = useState()
  const [selectedCategoria, setSelectedCategoria] = useState()
  const [selectedSquadra, setSelectedSquadra] = useState()
  const [selectedGaraInfo, setSelectedGaraInfo] = useState()
  const [selectedGiornoOrari, setSelectedGiornoOrari] = useState()

  const [squadraCasa, setSquadraCasa] = useState()
  const [squadraTrasferta, setSquadraTrasferta] = useState()

  // Set categorie
  useEffect(() => {
    if (open) setAvailableCategorie(getCategorie(availableGare))
  }, [availableGare, open])

  // Set gare
  useEffect(() => {
    setAvailableGareCategoria(
      availableGare
        ?.filter(a => a.categoria === selectedCategoria)
        ?.sort((a, b) => sortNumeriGara(a.numeroGara, b.numeroGara)),
    )
  }, [availableGare, selectedCategoria])

  // Set squadre
  useEffect(() => {
    setAvailableSquadre(
      iscrizioniLight
        ?.filter(
          i =>
            (availableGareCategoria
              ?.map(a => a.squadraCasaID)
              ?.includes(i.id) ||
              availableGareCategoria
                ?.map(a => a.squadraTrasfertaID)
                ?.includes(i.id)) &&
            String(i.codiceSocieta) === codiceUtente,
        )
        ?.map(i => i.id),
    )
  }, [availableGareCategoria, codiceUtente, iscrizioniLight])

  // Set gare
  useEffect(() => {
    setAvailableGareTeam(
      availableGare
        ?.filter(
          a =>
            a.squadraCasaID === selectedSquadra ||
            a.squadraTrasfertaID === selectedSquadra,
        )
        ?.sort((a, b) => sortNumeriGara(a.numeroGara, b.numeroGara)),
    )
  }, [availableGare, selectedSquadra])

  // Set gara
  useEffect(() => {
    if (!admin) {
      if (!spostamento?.selectedGara && availableGareTeam?.length === 1)
        setSpostamentoField('selectedGara', availableGareTeam[0]._id)
      if (
        spostamento?.selectedGara &&
        !availableGareTeam?.map(i => i._id)?.includes(spostamento?.selectedGara)
      )
        setSpostamentoField('selectedGara')
    }
  }, [admin, availableGareTeam, setSpostamentoField, spostamento])

  // Update selectedGara
  useEffect(() => {
    if (
      admin &&
      spostamento?.selectedGara &&
      !availableGareCategoria
        ?.map(i => i._id)
        ?.includes(spostamento?.selectedGara)
    )
      setSpostamentoField('selectedGara')
  }, [
    admin,
    availableGareCategoria,
    setSpostamentoField,
    spostamento?.selectedGara,
  ])

  // Update codiceSocietaDomanda
  useEffect(() => {
    if (
      admin &&
      spostamento?.codiceSocietaDomanda &&
      !availableAffiliazioni
        ?.map(i => i.codiceSocieta)
        ?.includes(spostamento?.codiceSocietaDomanda)
    )
      setSpostamentoField('codiceSocietaDomanda')
  }, [
    admin,
    availableAffiliazioni,
    setSpostamentoField,
    spostamento?.codiceSocietaDomanda,
  ])

  // Update gara info
  useEffect(() => {
    setSelectedGaraInfo(
      availableGare?.find(c => c._id === spostamento?.selectedGara),
    )
  }, [availableGare, spostamento])

  // Set team names
  useEffect(() => {
    if (selectedGaraInfo?.squadraCasaID)
      setSquadraCasa(
        iscrizioniLight?.find(i => i.id === selectedGaraInfo.squadraCasaID),
      )
    if (selectedGaraInfo?.squadraTrasfertaID)
      setSquadraTrasferta(
        iscrizioniLight?.find(
          i => i.id === selectedGaraInfo.squadraTrasfertaID,
        ),
      )
  }, [iscrizioniLight, selectedGaraInfo])

  // Update affiliazioni
  useEffect(() => {
    setAvailableAffiliazioni(
      affiliazioniLight?.filter(
        i =>
          i.codiceSocieta === squadraCasa?.codiceSocieta ||
          i.codiceSocieta === squadraTrasferta?.codiceSocieta,
      ),
    )
  }, [affiliazioniLight, squadraCasa, squadraTrasferta])

  // Set function
  useEffect(() => {
    if (
      spostamento?.selectedGara &&
      availableFunctions?.length &&
      !spostamento?.selectedFunction
    )
      setSpostamentoField('selectedFunction', availableFunctions[0])
  }, [availableFunctions, setSpostamentoField, spostamento])

  // Set selectedGiorno
  useEffect(() => {
    const iGiorno = fromStringToDate(spostamento?.newData)?.getDay()
    const selectedGiorno = GIORNI[(iGiorno + 6) % 7]
    setSelectedGiornoOrari(OrariPartite?.find(i => i.giorno === selectedGiorno))
  }, [OrariPartite, spostamento?.newData])

  // Reset inputs
  useEffect(() => {
    if (!spostamento?.selectedGara) {
      if (spostamento?.newData) setSpostamentoField('newData')
      if (spostamento?.newOra) setSpostamentoField('newOra')
    }
  }, [setSpostamentoField, spostamento])

  useEffect(() => {
    if (!open) {
      setSelectedCategoria()
      setSelectedSquadra()
      setSpostamento()
    }
  }, [open])

  const GridWarning = ({ text }) => (
    <Grid size={12}>
      <center>
        <b>
          <i>{text}</i>
        </b>
      </center>
    </Grid>
  )

  return (
    <Dialog
      open={open}
      handleClose={handleClose}
      triggerFunction={() => triggerFunction(spostamento)}
      title={
        admin
          ? titlesIscrizioni.titleSpostamentoInfo
          : titlesIscrizioni.titleSpostamentoDomanda
      }
      textUndo={admin ? 'Annulla' : 'Chiudi'}
      textConfirm='Conferma'
      maxWidth='lg'
      stretchHeight
    >
      <Grid container spacing={2} sx={{ width: '100%' }}>
        <Grid size={12}>
          {(admin && (
            <>
              Il presente modulo permette di richiedere uno spostamento per
              conto di una società.
              <br />
              Questa funzionalità è pensata per <b>casi eccezionali</b> in cui
              la società richiedente non può inserire lo spostamento da sè.
              <br />
              La compilazione del modulo genera una mail alla squadra
              avversaria, che dovrà accettare o rifiutare la richiesta.
            </>
          )) || (
            <>
              Il presente modulo permette di richiedere lo spostamento di una
              gara. La compilazione del modulo genera una mail alla squadra
              avversaria, che dovrà accettare o rifiutare la richiesta. Lo
              spostamento avrà luogo solo se la squadra avversaria accetta.
              <br />
              <br />
              <b>
                La richiesta va compilata
                {GiorniAnticipoSpostamento
                  ? ` almeno ${GiorniAnticipoSpostamento} giorni `
                  : ' '}
                prima dell'orario di inizio gara.
              </b>
              <br />
              <br />
              Per domande o chiarimenti scrivere a{' '}
              {mailTo(contatti?.MailSpostamenti)}.
            </>
          )}
        </Grid>
        {((!iscrizioniLight || iscrizioniLight.length === 0) && (
          <Grid size={12}>Nessuna squadra iscritta a questo campionato.</Grid>
        )) ||
          (isLoading && <GridCircularProgress />) ||
          (!availableGare?.length && (
            <GridWarning text='Nessuno spostamento possibile.' />
          )) || (
            <>
              {(admin && (
                <Grid container spacing={2} sx={{ width: '100%' }}>
                  <Grid size={{ xs: 12, md: 3 }}>
                    <Select
                      id='categoria'
                      label='Categoria'
                      value={selectedCategoria}
                      options={availableCategorie}
                      onChange={i => {
                        setSpostamentoField('codiceSocietaDomanda')
                        setSpostamentoField('selectedGara')
                        setSelectedCategoria(i)
                      }}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 3 }}>
                    <AutoComplete
                      id='gara'
                      label='Gara'
                      value={spostamento?.selectedGara}
                      options={availableGareCategoria?.map(i => i._id)}
                      getOptionLabel={i =>
                        availableGareCategoria?.find(a => a._id === i)
                          ?.numeroGara
                      }
                      onChange={i => {
                        setSpostamentoField('codiceSocietaDomanda')
                        setSpostamentoField('selectedGara', i)
                      }}
                      disabled={!selectedCategoria}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Select
                      id='codiceSocietaDomanda'
                      label='Società richiedente'
                      value={spostamento?.codiceSocietaDomanda}
                      options={availableAffiliazioni?.map(i => i.codiceSocieta)}
                      optionLabels={availableAffiliazioni?.map(
                        a => `${a.codiceSocieta} - ${a.nomeSocieta}`,
                      )}
                      onChange={i =>
                        setSpostamentoField('codiceSocietaDomanda', i)
                      }
                      disabled={!spostamento?.selectedGara}
                    />
                  </Grid>
                </Grid>
              )) || (
                <Grid container spacing={2} sx={{ width: '100%' }}>
                  <Grid size={{ xs: 12, md: 4 }}>
                    <Select
                      label='Categoria'
                      value={selectedCategoria}
                      options={availableCategorie}
                      onChange={setSelectedCategoria}
                      disabled={!availableCategorie}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Select
                      label='Squadra'
                      value={selectedSquadra}
                      options={availableSquadre}
                      optionLabels={availableSquadre?.map(
                        x =>
                          iscrizioniLight?.find(i => i.id === x)?.nomeSquadra,
                      )}
                      onChange={setSelectedSquadra}
                      disabled={!selectedCategoria || !availableSquadre}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 2 }}>
                    <Select
                      label='Gara'
                      value={spostamento?.selectedGara}
                      options={availableGareTeam?.map(i => i._id)}
                      optionLabels={availableGareTeam?.map(i => i.numeroGara)}
                      onChange={i => setSpostamentoField('selectedGara', i)}
                      disabled={!selectedSquadra || !availableGareTeam}
                    />
                  </Grid>
                </Grid>
              )}
              {spostamento?.selectedGara && (
                <Grid
                  container
                  spacing={3}
                  alignItems='center'
                  sx={{ width: '100%' }}
                >
                  <Grid size={{ xs: 12, md: 6 }}>
                    <center>
                      <b>
                        {squadraCasa?.nomeSquadra ||
                          selectedGaraInfo?.squadraCasaID}
                      </b>
                      &nbsp;-&nbsp;
                      <b>
                        {squadraTrasferta?.nomeSquadra ||
                          selectedGaraInfo?.squadraTrasfertaID}
                      </b>
                    </center>
                  </Grid>
                  <Grid size={{ xs: 6, md: 3 }}>
                    <center>
                      {selectedGaraInfo?.data || 'Data da definire'}
                    </center>
                  </Grid>
                  <Grid size={{ xs: 6, md: 3 }}>
                    <center>
                      {selectedGaraInfo?.ora || 'Ora da definire'}
                    </center>
                  </Grid>
                </Grid>
              )}
              {!admin &&
                !selectedCampionatoSpostamentiGratuiti &&
                spostamento?.selectedGara &&
                TIPOLOGIE_SPOSTAMENTO[spostamento?.selectedFunction]
                  ?.requiresPayment &&
                (!!selectedGaraInfo?.needsReferee ||
                  !!selectedGaraInfo?.requiresReferee) &&
                selectedGaraInfo?.girone !== 'finali' &&
                (!selectedGaraInfo?.data ||
                  !selectedGaraInfo?.ora ||
                  selectedGaraInfo?.data !== spostamento?.newData ||
                  !OreDiffSpostamentoGratuito ||
                  Math.abs(
                    fromStringToDecimalHours(selectedGaraInfo?.ora) -
                      fromStringToDecimalHours(spostamento?.newOra),
                  ) > OreDiffSpostamentoGratuito) && (
                  <GridCheckbox
                    label='La società si impegna a versare la tassa per lo spostamento.'
                    value={!!spostamento?.checkPayment}
                    onChange={(_, x) => setSpostamentoField('checkPayment', x)}
                    md={12}
                    justifyContent='flex-start'
                  />
                )}
              {spostamento?.selectedGara && (
                <Grid
                  container
                  spacing={2}
                  alignItems='center'
                  sx={{ width: '100%' }}
                >
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Select
                      id='tipologia'
                      value={spostamento?.selectedFunction}
                      options={availableFunctions}
                      optionLabels={availableFunctionsLabels}
                      label='Tipologia'
                      onChange={value =>
                        setSpostamentoField('selectedFunction', value)
                      }
                    />
                  </Grid>
                  {TIPOLOGIE_SPOSTAMENTO[spostamento?.selectedFunction]
                    ?.requiresNewDate && (
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                      <center>
                        <DatePicker
                          label='Nuova data'
                          value={spostamento?.newData}
                          onChange={i => setSpostamentoField('newData', i)}
                          onlyDays={availableGiorni}
                          disablePast={!admin}
                        />
                      </center>
                    </Grid>
                  )}
                  {TIPOLOGIE_SPOSTAMENTO[spostamento?.selectedFunction]
                    ?.requiresNewTime && (
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                      <center>
                        <TimePicker
                          label='Nuovo orario'
                          value={spostamento?.newOra}
                          onChange={i => setSpostamentoField('newOra', i)}
                          minTime={selectedGiornoOrari?.dalle}
                          maxTime={selectedGiornoOrari?.alle}
                        />
                      </center>
                    </Grid>
                  )}
                </Grid>
              )}
            </>
          )}
      </Grid>
    </Dialog>
  )
}

export default DialogSpostamento
